import { useAppearanceConfigModelStore } from '@/models/appearance-config/appearanceConfig';
import { useClientI18nModelStore } from '@/models/client-i18n/clientI18n';
import { useDocsDataModelStore } from '@/models/docs-data/docsData';
import { useEnvConfigModelStore } from '@/models/env-config/envConfig';
import { requestSendEmailCode } from '@/services/docs-site-auth/requestSendEmailCode';
import { requestVerifyEmailCode } from '@/services/docs-site-auth/requestVerifyEmailCode';
import type { DocsSiteVisitType } from '@/shared/constant/DocsIdTypeData';
import InnerAsyncMarkdownViewer from '@/ui-kit/components/MarkdownSafeMdxViewer/viewer';
import type { DocsSiteVisibilitySettings } from '@tarslib/types/lib/publishedProject';
import { Button, Form, Input, message, Spin } from 'aui';
import clsx from 'clsx';
import { useState } from 'react';
import CaptchaFormItem from './Captcha';
import styles from './DocsSiteAuth.module.css';

export enum EmailSettingsSubtitleType {
  DEFAULT = 'DEFAULT',
  CUSTOM = 'CUSTOM',
}

export enum EmailWhitelistMessageType {
  DEFAULT = 'DEFAULT',
  CUSTOM = 'CUSTOM',
}

const EmailValidator = (props: {
  id: number | string;
  imgRender: () => JSX.Element;
  name?: string;
  description?: string;
  projectId: number;
  subdirectory?: string;
  visitType?: DocsSiteVisitType;
  visibilitySettings: DocsSiteVisibilitySettings;
}) => {
  const appTheme = useAppearanceConfigModelStore((x) => x.appTheme);
  const theme = useDocsDataModelStore((x) => x.docsBaseConfig.theme);
  const clientI18n = useClientI18nModelStore((x) => x.clientI18n);
  const [messageApi, contextHolder] = message.useMessage();
  const [emailForm] = Form.useForm();
  const [sendEmailCodeLoading, setSendEmailCodeLoading] = useState<boolean>(false);
  const envConfig = useEnvConfigModelStore((x) => x);
  const appLowerName = useEnvConfigModelStore((x) => x.APP_NAME_LOWER);
  const {
    id,
    imgRender,
    name,
    projectId,
    subdirectory,
    visitType,
    visibilitySettings,
    description,
  } = props;
  const emailSettings = visibilitySettings.advancedSettings?.emailSettings;
  const subtitleType = emailSettings?.subtitleType;
  const customSubtitle = emailSettings?.subtitle;
  const emailInWhitelistMessageType = emailSettings?.emailInWhitelistMessageType;
  const emailNotInWhitelistMessageType = emailSettings?.emailNotInWhitelistMessageType;
  const emailInWhitelistMessage = emailSettings?.emailInWhitelistMessage;
  const emailNotInWhitelistMessage = emailSettings?.emailNotInWhitelistMessage;

  const verifyEmailCodeAndNav = async (email: string, code: string) => {
    const [error] = await requestVerifyEmailCode(
      {
        id,
        projectId,
        email,
        code,
      },
      envConfig,
      clientI18n,
      visitType,
      subdirectory,
    );
    if (!error) {
      window.location.reload();
    }
  };

  const onSend = async (email: string) => {
    let result = false;
    setSendEmailCodeLoading(true);
    const [error] = await requestSendEmailCode(
      { id, projectId, email },
      envConfig,
      clientI18n,
      visitType,
      subdirectory,
    );
    setSendEmailCodeLoading(false);
    if (!error) {
      result = true;
      if (emailInWhitelistMessageType === EmailWhitelistMessageType.CUSTOM) {
        messageApi.info({
          content: (
            <InnerAsyncMarkdownViewer
              value={emailInWhitelistMessage}
              inDocView={false}
              renderExternalLink={(url: string) => {
                return url;
              }}
              appLowerName={appLowerName}
              theme={appTheme ?? theme}
            />
          ),
          className: styles.emailValidatorCustomError,
          icon: <></>,
        });
      } else {
        message.success(clientI18n('onlineShare.sendCodeSuccess'));
      }
    } else {
      result = false;
      if (emailNotInWhitelistMessageType === EmailWhitelistMessageType.CUSTOM) {
        messageApi.info({
          content: (
            <InnerAsyncMarkdownViewer
              value={emailNotInWhitelistMessage}
              inDocView={false}
              renderExternalLink={(url: string) => {
                return url;
              }}
              appLowerName={appLowerName}
              theme={appTheme ?? theme}
            />
          ),
          className: styles.emailValidatorCustomError,
          icon: <></>,
        });
      } else {
        message.success(clientI18n('onlineShare.sendCodeSuccess'));
      }
    }
    return result;
  };

  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async ({ email, code }: { email: string; code: string }) => {
    setSubmitting(true);
    await verifyEmailCodeAndNav(email, code);
    setSubmitting(false);
  };

  const renderSubtitle = () => {
    if (subtitleType === EmailSettingsSubtitleType.CUSTOM) {
      return (
        <InnerAsyncMarkdownViewer
          value={customSubtitle}
          inDocView={false}
          renderExternalLink={(url: string) => {
            return url;
          }}
          appLowerName={appLowerName}
          theme={appTheme ?? theme}
        />
      );
    }
    return <>{clientI18n('email.auth.tip')}</>;
  };

  return (
    <div className={clsx('flex max-w-[560px] flex-col items-center justify-center')}>
      {contextHolder}
      {imgRender()}
      <div className="mb-3 text-center text-h2 font-600 text-color">{name}</div>
      {!!description && (
        <div className="pb-3 text-center text-lg font-400 text-color-sub">{description}</div>
      )}
      <div
        className={clsx(styles.description, 'mb-6 mt-3 text-center text-lg font-400 text-color')}
      >
        {renderSubtitle()}
      </div>

      <Spin spinning={submitting}>
        <Form onFinish={handleSubmit} className="w-[360px]" form={emailForm}>
          <Form.Item
            name="email"
            style={{ marginBottom: 16 }}
            validateTrigger={['onSubmit']}
            rules={[
              {
                required: true,
                message: clientI18n('user.loginOrRegister.rule.required.mail'),
              },
              {
                type: 'email',
                message: clientI18n('user.loginOrRegister.rule.pattern.mail'),
              },
            ]}
          >
            <Input size="large" allowClear placeholder={clientI18n('user.loginOrRegister.email')} />
          </Form.Item>
          <CaptchaFormItem
            inputProps={{ autoFocus: false }}
            sendTxt={clientI18n('user.loginOrRegister.captcha.sendEmail')}
            onStart={async () => {
              await emailForm.validateFields(['email']);
              const success = await onSend(emailForm.getFieldValue('email'));
              return success;
            }}
            loading={sendEmailCodeLoading}
          />
          <Button type="primary" block htmlType="submit" size="large" loading={submitting}>
            {clientI18n('view')}
          </Button>
        </Form>
      </Spin>
    </div>
  );
};

export default EmailValidator;
